// Share the company standard rate card with one named person (Session RCS, Director, 27 October 2026).
//
// Opened from the Documents menu (admin only, as the rate card itself is). The admin types who the link is for and gets a
// link that opens the standard card as a PDF without a sign-in, for 7 days or until it is switched off. The link is shown
// ONCE, here, because the platform keeps only its hash; losing it means making another. Below, every link made, with
// whether it still opens and how often it was opened, and the two-step Switch off and Delete. Nothing is sent from here:
// the admin pastes the link into their own message.
import { useCallback, useEffect, useState } from "react";
import { ModalShell, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "./ModalShell";
import { ConfirmButton } from "./ConfirmButton";
import { api, ApiError, type RateCardShareLink } from "../lib/api";
import { formatDate, formatDateTime } from "../lib/format";
import { Loader } from "./Loader";

const STATE_WORDS: Record<RateCardShareLink["state"], string> = {
  live: "Opens",
  expired: "Expired",
  switched_off: "Switched off",
};

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

export function RateCardShareModal({ onClose }: { onClose: () => void }) {
  const [links, setLinks] = useState<RateCardShareLink[] | null>(null);
  const [days, setDays] = useState(7);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ url: string; name: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.documents.rateCardLinks.list();
      setLinks(res.links);
      setDays(res.days);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err, "Could not load the links. Please try again in a moment."));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function make() {
    const who = name.trim();
    if (!who) {
      setError("Say who the link is for.");
      return;
    }
    setBusy(true);
    setError(null);
    setCopied(null);
    try {
      const res = await api.documents.rateCardLinks.create(who);
      setMade({ url: res.url, name: res.link.recipientName, expiresAt: res.link.expiresAt });
      setName("");
      await load();
    } catch (err) {
      setError(errorText(err, "The link was not made. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied("yes");
    } catch {
      setCopied("failed");
    }
  }

  async function revoke(id: string) {
    setRowBusy(id);
    try {
      await api.documents.rateCardLinks.revoke(id);
      await load();
    } catch (err) {
      setLoadError(errorText(err, "The link was not switched off. Please try again."));
    } finally {
      setRowBusy(null);
    }
  }

  async function remove(id: string) {
    setRowBusy(id);
    try {
      await api.documents.rateCardLinks.remove(id);
      await load();
    } catch (err) {
      setLoadError(errorText(err, "The record was not deleted. Please try again."));
    } finally {
      setRowBusy(null);
    }
  }

  return (
    <ModalShell ariaLabel="Share the rate card" onClose={() => !busy && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="flex max-h-[85vh] flex-col p-5">
      <h2 className="mb-1 font-heading text-xl text-ele-strong">Share the rate card</h2>
      <p className="mb-3 text-sm text-ele-body">
        Make a private link to the company standard rate card for one person. It opens without a sign-in for {days} days, or until you
        switch it off. Nothing is sent: paste the link into your own message.
      </p>
      <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted" htmlFor="rate-card-share-name">
        Who is it for?
      </label>
      <div className="mb-2 flex flex-wrap gap-2">
        <input
          id="rate-card-share-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void make();
          }}
          maxLength={120}
          placeholder="Name, and company if any"
          className="min-h-11 min-w-0 flex-1 rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green"
        />
        <button type="button" onClick={() => void make()} disabled={busy} className={MODAL_PRIMARY_BTN}>
          {busy ? "Making link..." : "Make link"}
        </button>
      </div>
      {error && (
        <p role="alert" className="mb-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {made && (
        <div className="mb-3 rounded-ele border border-ele-green p-3" data-testid="rate-card-share-made">
          <p className="mb-2 text-sm text-ele-strong">
            Link for {made.name}. It opens until {formatDate(made.expiresAt)}. This is the only time it is shown.
          </p>
          <input
            readOnly
            value={made.url}
            aria-label="The link"
            onFocus={(e) => e.currentTarget.select()}
            className="mb-2 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-xs text-ele-strong"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void copy(made.url)} className={MODAL_PRIMARY_BTN}>
              Copy link
            </button>
            {copied === "yes" && (
              <span role="status" className="text-sm text-ele-body">
                Copied.
              </span>
            )}
            {copied === "failed" && (
              <span role="status" className="text-sm text-ele-body">
                Could not copy. Select the link above and copy it.
              </span>
            )}
          </div>
        </div>
      )}
      <h3 className="mb-1 mt-1 font-menu text-xs uppercase tracking-wide text-ele-muted">Links made</h3>
      {loadError && (
        <p role="alert" className="mb-2 text-sm text-ele-danger">
          {loadError}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto rounded-ele border border-ele-purple-accent" data-vscroll>
        {links === null && !loadError ? (
          <Loader label="Loading the links..." rows={2} className="p-3" />
        ) : links && links.length === 0 ? (
          <p className="p-3 text-sm text-ele-body">No links made yet.</p>
        ) : (
          <ul className="divide-y divide-ele-purple-accent">
            {(links ?? []).map((l) => (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="text-ele-strong">{l.recipientName}</span>
                  <span className="block text-xs text-ele-body">
                    {STATE_WORDS[l.state]}
                    {l.state === "live" ? ` until ${formatDate(l.expiresAt)}` : ""}. Made {formatDate(l.createdAt)}.{" "}
                    {l.openCount === 0
                      ? "Not opened yet."
                      : `Opened ${l.openCount} ${l.openCount === 1 ? "time" : "times"}, last ${formatDateTime(l.lastOpenedAt ?? l.createdAt)}.`}
                  </span>
                </span>
                {l.state === "live" ? (
                  <ConfirmButton
                    onConfirm={() => void revoke(l.id)}
                    label="Switch off"
                    confirmLabel="Switch off?"
                    busy={rowBusy === l.id}
                    busyLabel="Switching off..."
                    className={MODAL_SECONDARY_BTN}
                  />
                ) : (
                  <ConfirmButton
                    onConfirm={() => void remove(l.id)}
                    label="Delete"
                    confirmLabel="Delete record?"
                    busy={rowBusy === l.id}
                    className={MODAL_SECONDARY_BTN}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={onClose} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Close
        </button>
      </div>
    </ModalShell>
  );
}
